/**
 * The LOI tab (docs/ux/loi.md): the state machine over the review head and
 * the extraction job: no letter, no extraction, extracting, extraction
 * failed, review, approved. Two panes: the letter (sticky) on the left, the
 * form on the right; the extraction strip and the review bar above. Owns
 * `?item=` (a field key or `q:` plus a question id) and `?view=changes`.
 */

import { cn } from 'cn';
import { Suspense, lazy, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { say } from '@/app/announcer';
import { useEnvironment } from '@/app/environment';
import { useSession } from '@/app/session';
import type { LoiFieldDecisionInput, LoiQuestionDecisionInput, MatterDetail } from '@/app/types';
import { useIsPhone } from '@/app/viewport';
import { EmptyState, ErrorPanel } from '@/components/shell/states';
import { useQueryClient } from '@tanstack/react-query';
import { AttachLetter } from '../../intake/AttachLetter';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ExtractionPanel, extractStateOf } from './ExtractionPanel';
import { FieldTable } from './FieldTable';
import { LoiReviewBar, ReextractDialog } from './LoiReviewBar';
import { FieldCards, LetterCollapsible, LoiStateCard, QuestionCards } from './Phone';
import { QuestionTree } from './QuestionTree';
import { ApprovedForm, WhatChanged } from './WhatChanged';
import { blockingItems, countsOf, questionName, undecidedItems } from './describe';
import { extracting as isExtracting, letterPagesUrl, useApproveLoi, useDecideField, useDecideQuestion, useExtractLoi, useLoi, useLoiDiff, useStartLoiReview } from './useLoi';

const PageRangeViewer = lazy(() => import('../casefile/PageRangeViewer'));

export function LoiTab({ matterRef, detail, offline }: { matterRef: string; detail: MatterDetail | undefined; offline: boolean }) {
  const phone = useIsPhone();
  const client = useQueryClient();
  const { me } = useSession();
  const environment = useEnvironment();
  const [params, setParams] = useSearchParams();
  const loi = useLoi(matterRef, detail !== undefined);
  const view = loi.data;
  const head = view?.head;
  const approved = head?.state === 'approved';
  const locked = approved && head?.approvedAt ? { by: head.approvedByName ?? head.approvedBy ?? 'someone', at: head.approvedAt } : null;
  const recorded = (environment.data?.loiMode ?? 'recorded') === 'recorded';
  const extractState = view ? extractStateOf(view) : 'none';

  const itemParam = params.get('item');
  const viewParam = params.get('view');
  const showChanges = viewParam === 'changes';
  const [refused, setRefused] = useState<{ reasons: string[]; at: string } | null>(null);
  const [reextractOpen, setReextractOpen] = useState(false);
  const [emphasis, setEmphasis] = useState<{ page: number; label: string } | null>(null);
  const [scrollTo, setScrollTo] = useState<number | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [failedKey, setFailedKey] = useState<string | null>(null);
  const statusRef = useRef<HTMLSpanElement>(null);
  // The bar is sticky under the hub's compact strip; the letter pane sits under the bar, so its offset follows the bar's measured height.
  const barRef = useRef<HTMLDivElement>(null);
  const [barHeight, setBarHeight] = useState(0);
  useLayoutEffect(() => {
    const el = barRef.current;
    if (!el) { setBarHeight(0); return; }
    const measure = () => setBarHeight(el.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [view?.extraction?.runId]);
  // A 422 on a decision: the server's reason shows under the control of the item it refused.
  const [refusedItem, setRefusedItem] = useState<{ key: string; reason: string } | null>(null);
  // The first undecided item opens on load (the first blocking one while fields could block, D-0032 made only questions block, and the reviewer still reads the form top to bottom); once the reviewer selects, closes or decides anything, the tab stops choosing for them.
  const [touched, setTouched] = useState(false);

  const fields = view?.fields ?? [];
  const questions = view?.questions ?? [];
  const counts = useMemo(() => countsOf(fields, questions), [fields, questions]);
  const blocking = useMemo(() => blockingItems(fields, questions), [fields, questions]);
  const undecided = useMemo(() => undecidedItems(fields, questions), [fields, questions]);
  const rerun = fields.some((f) => f.item?.prior);
  const defaultItem = phone || approved || !view?.extraction ? null : undecided[0] ?? blocking[0] ?? null;
  const selected = itemParam && (fields.some((f) => f.key === itemParam) || (itemParam.startsWith('q:') && questions.some((q) => `q:${q.id}` === itemParam))) ? itemParam : showChanges || touched ? null : defaultItem;
  const selectedField = selected && !selected.startsWith('q:') ? selected : null;
  const selectedQuestion = selected?.startsWith('q:') ? selected.slice(2) : null;
  const diff = useLoiDiff(matterRef, Boolean(view?.extraction) && (showChanges || phone));

  function setItem(key: string | null) {
    setTouched(true);
    setParams((p) => { p.delete('view'); if (key) p.set('item', key); else p.delete('item'); return p; }, { replace: true });
  }
  function setView(v: 'review' | 'changes') {
    setParams((p) => { if (v === 'changes') { p.set('view', 'changes'); p.delete('item'); } else p.delete('view'); return p; }, { replace: true });
  }

  const start = useStartLoiReview(matterRef);
  const decideField = useDecideField(matterRef, { onRefused: (key, reasons) => setRefusedItem({ key, reason: reasons[0] ?? 'The server refused the decision.' }) });
  const decideQuestion = useDecideQuestion(matterRef, { onRefused: (id, reasons) => setRefusedItem({ key: `q:${id}`, reason: reasons[0] ?? 'The server refused the decision.' }) });
  const approve = useApproveLoi(matterRef, {
    onRefused: (reasons) => setRefused({ reasons, at: new Date().toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: false }) }),
    onApproved: () => { setRefused(null); setItem(null); requestAnimationFrame(() => statusRef.current?.focus()); },
  });
  const extract = useExtractLoi(matterRef);
  const [awaitingJob, setAwaitingJob] = useState<string | null>(null);
  const runExtraction = () => { say('Extraction started.'); extract.mutate(undefined, { onSuccess: (job) => setAwaitingJob(job.jobId) }); };

  const ensureStarted = () => { if (head && !head.startedAt && !head.approvedAt && head.extractionRunId) start.mutate(); };

  // The outcome of the extraction this tab started, once the view carries that job.
  useEffect(() => {
    if (!view || !awaitingJob || head?.extract?.jobId !== awaitingJob) return;
    const state = head.extract.state;
    if (state === 'succeeded' && view.extraction) {
      setAwaitingJob(null);
      setTouched(false);
      const s = view.head.summary;
      const text = `Extraction complete: ${s?.fields ?? view.fields.length} fields, ${view.tree.length} questions, ${view.standingInstructions.length} standing ${view.standingInstructions.length === 1 ? 'instruction' : 'instructions'}. ${blocking.length} ${blocking.length === 1 ? 'item' : 'items'} to decide.`;
      toast.success(text);
      say(text);
    } else if (state === 'failed') {
      setAwaitingJob(null);
      toast.error('The extraction did not complete. The letter is unchanged.', { action: { label: 'Run extraction again', onClick: runExtraction } });
      say('The extraction did not complete.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [head?.extract?.jobId, head?.extract?.state, awaitingJob, view?.extraction?.runId]);

  function onShowPage(page: number, label: string) {
    setEmphasis({ page, label });
    setScrollTo(page);
    // A second click on the same page must scroll again.
    requestAnimationFrame(() => setScrollTo(null));
    // The viewer shows the first twelve pages; a citation beyond them is named, not claimed to be scrolled to.
    const shown = page <= Math.min(view?.letterPages ?? 0, 12);
    say(shown ? `Letter scrolled to page ${page}, the evidence cited for ${label}.` : `Page ${page} is cited for ${label}; it is beyond the pages shown here.`);
  }

  function next() {
    const list = undecided.filter((k) => k !== selected);
    const after = selected ? list.find((k) => undecided.indexOf(k) > undecided.indexOf(selected)) : undefined;
    const target = after ?? list[0] ?? null;
    if (!target) return;
    setItem(target);
    requestAnimationFrame(() => { const el = document.getElementById(target.startsWith('q:') ? `loi-q-${encodeURIComponent(target.slice(2))}` : `loi-row-${target}`); el?.scrollIntoView({ block: 'center' }); el?.focus(); });
  }

  function pin() {
    if (!itemParam && selected) setParams((p) => { p.set('item', selected); return p; }, { replace: true });
    setTouched(true);
  }

  function onDecideField(key: string, input: LoiFieldDecisionInput) {
    const field = fields.find((f) => f.key === key);
    pin();
    ensureStarted();
    setBusyKey(key); setFailedKey(null); setRefusedItem(null);
    decideField.mutate({ key, input, label: field?.label ?? key }, {
      onSuccess: (item) => {
        setRefused(null);
        const remaining = blocking.filter((k) => k !== key).length + (item.decision === 'flagged' || (field?.required && item.decision === 'missing') ? 1 : 0);
        say(`Decision saved: ${field?.label ?? key} ${item.decision}. ${remaining === 0 ? 'Every item is decided.' : `${remaining} ${remaining === 1 ? 'item blocks' : 'items block'} approval.`}`);
      },
      onError: () => setFailedKey(key),
      onSettled: () => setBusyKey(null),
    });
  }

  function onDecideQuestion(id: string, input: LoiQuestionDecisionInput) {
    const q = questions.find((x) => x.id === id);
    pin();
    ensureStarted();
    setBusyKey(`q:${id}`); setFailedKey(null); setRefusedItem(null);
    decideQuestion.mutate({ id, input, label: q ? questionName(q).toLowerCase() : `question ${id}` }, {
      onSuccess: (item) => {
        setRefused(null);
        const remaining = blocking.filter((k) => k !== `q:${id}`).length + (item.decision === 'flagged' ? 1 : 0);
        say(`Decision saved: question ${id} ${item.decision}. ${remaining === 0 ? 'Every item is decided.' : `${remaining} ${remaining === 1 ? 'item blocks' : 'items block'} approval.`}`);
      },
      onError: () => setFailedKey(`q:${id}`),
      onSettled: () => setBusyKey(null),
    });
  }

  if (!detail) return <div className="flex flex-col gap-3"><Skeleton className="h-11 rounded-xl" /><Skeleton className="h-11 rounded-xl" /><Skeleton className="h-64 rounded-xl" /></div>;
  if (loi.isError && !view) return <ErrorPanel message="The LOI review could not be loaded." onRetry={() => void loi.refetch()} />;
  if (!view || !head) {
    return (
      <div className="flex flex-col gap-3" aria-busy="true">
        <Skeleton className="h-11 rounded-xl" />
        <Skeleton className="h-11 rounded-xl" />
        <div className="grid gap-6 xl:grid-cols-[560px_minmax(0,1fr)]"><Skeleton className="h-[560px] rounded-xl" /><div className="flex flex-col gap-px">{[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-14 rounded-none first:rounded-t-xl last:rounded-b-xl" />)}</div></div>
      </div>
    );
  }

  if (view.letterPages === null && !view.extraction) {
    return <AttachLetter matterRef={matterRef} onAttached={() => void client.invalidateQueries({ queryKey: ['matter', matterRef] })} />;
  }

  const letterPages = view.letterPages ?? 0;
  const letterPlan = { mode: 'range' as const, windows: [{ from: 1, to: Math.min(letterPages, 12) }], extent: { from: 1, to: letterPages }, captions: emphasis ? { [emphasis.page]: `Page ${emphasis.page}. Evidence cited for ${emphasis.label}` } : {}, dashedBefore: [] };
  const mine = Boolean(me && head.reviewer && (head.reviewer === me.sub || head.reviewer === me.actingAs));

  if (phone) {
    return (
      <div className="flex flex-col gap-3">
        <LoiStateCard view={view} counts={counts} recorded={recorded} diff={diff.data} />
        {view.extraction ? <><FieldCards view={view} /><QuestionCards view={view} /></> : null}
        {letterPages > 0 ? <LetterCollapsible matterRef={matterRef} pages={letterPages} /> : null}
      </div>
    );
  }

  const letterPane = letterPages > 0 ? (
    // Sticky under the hub's compact strip (52 + 44 px), the bar and the 16 px gap, so the letter stays beside the long form.
    <aside aria-label={`Letter of instruction, ${letterPages} ${letterPages === 1 ? 'page' : 'pages'}`} data-slot="letter-pane" className="xl:sticky xl:top-[calc(112px+var(--loi-bar-h))]">
      <Suspense fallback={<Skeleton className="h-[560px] rounded-xl" />}>
        <PageRangeViewer matterRef={matterRef} runId="letter" pagesUrl={(from, to) => letterPagesUrl(matterRef, from, to)} subject="the letter" heightClass="h-[560px] xl:h-[calc(100vh-136px-var(--loi-bar-h))] xl:min-h-[420px]" plan={letterPlan} yourBoundary={null} emphasisPage={emphasis?.page ?? null} scrollToPage={scrollTo} offline={offline} />
      </Suspense>
    </aside>
  ) : null;

  return (
    <div className="flex flex-col gap-3" data-slot="loi-tab" style={{ '--loi-bar-h': `${barHeight}px` } as CSSProperties}>
      <ExtractionPanel matterRef={matterRef} view={view} state={extractState} recorded={recorded} offline={offline} approved={Boolean(approved)} onExtract={runExtraction} onReextract={() => setReextractOpen(true)} busy={extract.isPending} stalled={isExtracting(head) && loi.failureCount >= 2} onCheckNow={() => void loi.refetch()} />
      {view.extraction ? (
        <div ref={barRef} className="z-20 xl:sticky xl:top-[96px]">
          <LoiReviewBar matterRef={matterRef} head={head} counts={counts} mine={mine} rerun={rerun} matterStatus={detail.matter.status} offline={offline} approving={approve.isPending} refused={refused} onApprove={async () => { setRefused(null); try { await approve.mutateAsync(); return true; } catch { return false; } }} statusRef={statusRef} extractionRunId={head.extractionRunId} letterPages={letterPages} recorded={recorded} />
        </div>
      ) : null}
      <div className={cn('mt-1 grid items-start gap-6', letterPane && 'xl:grid-cols-[520px_minmax(0,1fr)] 2xl:grid-cols-[560px_minmax(0,1fr)]')}>
        {letterPane}
        <div data-slot="form-pane" role="region" aria-label="Instruction form" className="flex min-w-0 flex-col gap-4">
          {!view.extraction ? (
            extractState === 'extracting' ? (
              <div className="flex flex-col gap-3"><p className="text-[13px] text-beam-text-muted">The form fills when the extraction completes.</p>{[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-14 rounded-xl" />)}</div>
            ) : (
              <EmptyState
                title="Nothing to review yet."
                description={<>Run the extraction to read the letter into Beam's instruction form. The machine's best pass appears here for review, field by field and question by question. Nothing is approved until you approve it.{recorded ? <span className="mt-2 block text-xs">This environment serves the recorded result for this letter (LOI: recorded). Live extraction runs where the portal is configured with an au.* inference profile.</span> : null}</>}
                action={extractState === 'failed' ? undefined : <Button size="lg" className="h-9" data-primary={offline ? undefined : 'true'} disabled={offline} aria-disabled={extract.isPending ? 'true' : undefined} onClick={() => { if (!extract.isPending) runExtraction(); }}>{extract.isPending ? 'Starting' : 'Run extraction'}</Button>}
              />
            )
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <Tabs value={showChanges ? 'changes' : 'review'} onValueChange={(v) => setView(v === 'changes' ? 'changes' : 'review')}>
                  <TabsList aria-label="Form view" className="h-10 gap-0.5 rounded-full border border-beam-line bg-beam-cream-deep p-1">
                    <TabsTrigger value="review" className="h-8 rounded-full px-3.5 text-sm text-beam-text-muted data-active:bg-beam-paper data-active:text-foreground data-active:shadow-beam-1">{approved ? 'Form' : 'Review'}</TabsTrigger>
                    <TabsTrigger value="changes" className="h-8 rounded-full px-3.5 text-sm text-beam-text-muted data-active:bg-beam-paper data-active:text-foreground data-active:shadow-beam-1">What changed <span className="tabular ml-1 text-beam-text-muted">{counts.corrected + counts.missing}</span></TabsTrigger>
                  </TabsList>
                </Tabs>
                {!approved && undecided.length > 0 && !showChanges ? <Button variant="ghost" size="default" className="h-8" onClick={next}>Next unresolved</Button> : null}
              </div>
              {showChanges ? (
                diff.isError ? <ErrorPanel message="What changed could not be loaded." onRetry={() => void diff.refetch()} /> : diff.data ? <WhatChanged rows={diff.data} fields={fields} questions={questions} groups={view.groups} head={head} runId={head.extractionRunId} locked={Boolean(approved)} onOpenItem={(k) => setItem(k)} /> : <Skeleton className="h-64 rounded-xl" />
              ) : approved && !selected ? (
                <ApprovedForm view={view} onOpenItem={(k) => setItem(k)} />
              ) : (
                <>
                  {extractState === 'extracting' ? <p role="status" className="text-[13px] text-beam-text-muted">A new extraction is running. Decisions pause until it completes.</p> : null}
                  <FieldTable matterRef={matterRef} fields={fields} groups={view.groups} selectedKey={selectedField} locked={locked} offline={offline || extractState === 'extracting'} busyKey={busyKey} saveFailedKey={failedKey} onSelect={(k) => setItem(k)} onDecide={onDecideField} onShowPage={onShowPage} onNext={next} hasNext={undecided.some((k) => k !== selected)} refusedReason={refusedItem} />
                  <QuestionTree matterRef={matterRef} questions={questions} tree={view.tree} standingInstructions={view.standingInstructions} selectedId={selectedQuestion} locked={locked} offline={offline || extractState === 'extracting'} busyId={busyKey?.startsWith('q:') ? busyKey.slice(2) : null} saveFailedId={failedKey?.startsWith('q:') ? failedKey.slice(2) : null} onSelect={(id) => setItem(id ? `q:${id}` : null)} onDecide={onDecideQuestion} onShowPage={onShowPage} onNext={next} hasNext={undecided.some((k) => k !== selected)} refusedReason={refusedItem?.key.startsWith('q:') ? { id: refusedItem.key.slice(2), reason: refusedItem.reason } : null} />
                  {approved && selected ? <Button variant="ghost" size="default" className="h-8 self-start" onClick={() => setItem(null)}>Back to the form</Button> : null}
                </>
              )}
            </>
          )}
        </div>
      </div>
      <ReextractDialog open={reextractOpen} onOpenChange={setReextractOpen} onConfirm={() => { setReextractOpen(false); runExtraction(); }} decided={counts.decided} runId={head.extractionRunId} pages={letterPages} recorded={recorded} busy={extract.isPending} approvedVersion={approved ? head.authoritativeVersion : null} />
    </div>
  );
}
